import Api from 'entities/helpers/api';
import EverTrue from 'app';
import { useState, useCallback, useEffect, useMemo } from 'react';

export const useLists = (type, ownedOnly, alreadyDefinedLists = null) => {
  const [total, setTotal] = useState(0);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enableAllActions, setEnableAllActions] = useState(false); // Boolean to enable all actions in the UI when all selected lists are owned by the user

  // Sorting
  // Only allow sorting by 'name' and 'updated_at'
  const sortableFields = useMemo(() => ['name', 'updated_at'], []);
  const storageKey = type === 'dynamic' ? 'EvertrueSSTableSettings' : 'EvertrueListsTableSettings';
  // Load sort preferences from localStorage if available
  const tableSettings = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey)) || {};
    } catch {
      return {};
    }
  }, [storageKey]);
  const initialSortField = sortableFields.includes(tableSettings.sortField) ? tableSettings.sortField : 'name';
  const initialSortOrder = tableSettings.sortOrder === 'desc' ? 'desc' : 'asc';
  const [sortModel, setSortModel] = useState([
    {
      field: initialSortField,
      sort: initialSortOrder,
    },
  ]);

  // Persist sort preferences to localStorage when sortModel changes
  useEffect(() => {
    if (sortModel && sortModel[0] && sortableFields.includes(sortModel[0].field)) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          sortField: sortModel[0].field,
          sortOrder: sortModel[0].sort,
        })
      );
    }
  }, [sortModel, sortableFields, storageKey]);

  // Search
  const [search, setSearch] = useState('');

  // Pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  /**
   * Updates the search query and resets pagination to page 0.
   * React 18 batches both setState calls so only one API request is fired.
   */
  const handleSearchChange = useCallback(
    (query) => {
      setSearch(query);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [] // setSearch and setPaginationModel are stable React dispatch functions
  );

  /**
   * Updates the sort model and resets pagination to page 0.
   * React 18 batches both setState calls so only one API request is fired,
   * preventing a race condition where two requests (old offset, then offset=0) could overlap.
   */
  const handleSortChange = useCallback(
    (newSortModel) => {
      setSortModel(newSortModel);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [] // setSortModel and setPaginationModel are stable React dispatch functions
  );

  // Row Selection
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const clearSelections = () => {
    setRowSelectionModel({
      type: 'include',
      ids: new Set(),
    });
  };
  // Check and see if user owns all selected lists
  const checkOwnership = (selectedIds, userId) => {
    // The row selection model only tracks IDs, but to check this we need the full list objects
    const selectedLists = lists.filter((list) => selectedIds.has(list.id));
    const allSelectedListsAreUserOwned =
      selectedLists.length > 0 && selectedLists.every((list) => list.user_id === userId);
    setEnableAllActions(allSelectedListsAreUserOwned);
  };
  const updateRowSelectionModel = (newModel, userId) => {
    setRowSelectionModel(newModel);
    checkOwnership(newModel.ids, userId);
  };

  // Retrieve lists from the API and update the state
  const getLists = useCallback(() => {
    setLoading(true);
    const params = {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    };
    if (type) {
      params.type = type;
    }
    if (ownedOnly) {
      params.owned_only = ownedOnly;
    }
    // Add sorting params if valid
    if (sortModel && sortModel[0] && sortableFields.includes(sortModel[0].field)) {
      params.sort_column = sortModel[0].field;
      params.sort_order = sortModel[0].sort;
    }
    if (search) {
      params.search = search;
    }
    Api.CONTACTS.LISTS.get({
      params,
    })
      .then((resp) => {
        setLists(resp?.items);
        setTotal(resp?.total);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLists([]);
        setTotal(0);
      });
  }, [paginationModel, type, ownedOnly, sortModel, sortableFields, search]);

  // Fetch Initial Lists if they have not already been defined
  useEffect(() => {
    if (type === null && alreadyDefinedLists) {
      setLists(alreadyDefinedLists);
      setTotal(alreadyDefinedLists.length);
    } else {
      getLists();
    }
  }, [type, alreadyDefinedLists, getLists]);

  // Delete a single list
  const deleteList = async (listId) => {
    // Call the API to delete the list
    const listName = lists.find((list) => list.id === listId)?.name || 'List';
    await Api.CONTACTS.LIST.delete({
      urlArgs: { list_id: listId },
      success_message: `${listName} deleted`,
      error_message: `Error while deleting list ${listName}`,
    });

    // Update the table to reflect the deletion
    setLists((prevLists) => prevLists.filter((list) => list.id !== listId));
    setTotal((prevTotal) => prevTotal - 1);
    //Delete list mixpanel event
    EverTrue.track.set('list_action', { type: 'delete' });
  };

  // Delete multiple lists
  const deleteLists = (listIds) => {
    const deletePromises = listIds.map((listId) =>
      Api.CONTACTS.LIST.delete({
        urlArgs: { list_id: listId },
      })
    );

    Promise.all(deletePromises)
      .then(() => {
        setTotal((prevTotal) => prevTotal - listIds.length);
        setLists((prevLists) => prevLists.filter((list) => !listIds.includes(list.id)));
        EverTrue.Alert.success(`Selected Lists deleted`);
      })
      .catch(() => {
        EverTrue.Alert.error('Error while deleting selected lists.');
      });
  };

  // Remove lists from a folder
  const removeListsFromFolder = async (folderId, listIds) => {
    const removePromises = listIds.map((listId) =>
      Api.CONTACTS.FOLDER_LISTS.delete({
        params: { folder_id: folderId, list_id: listId },
        disableAlerts: true,
      })
    );

    Promise.all(removePromises)
      .then(() => {
        setLists((prevLists) => prevLists.filter((list) => !listIds.includes(list.id)));
        setTotal((prevTotal) => prevTotal - listIds.length);
        EverTrue.Alert.success('Selected lists removed from folder');
      })
      .catch(() => {
        EverTrue.Alert.error('An error occurred while removing lists from folder.');
      });
  };

  return {
    total,
    lists,
    loading,
    enableAllActions,
    search,
    handleSearchChange,
    sortModel,
    setSortModel,
    handleSortChange,
    paginationModel,
    setPaginationModel,
    rowSelectionModel,
    updateRowSelectionModel,
    clearSelections,
    getLists,
    deleteList,
    deleteLists,
    removeListsFromFolder,
  };
};
